/**
 * OCP-236 — admin-side OAuth consent API client.
 *
 * Two calls the consent screen (`OAuthConsentPage`) makes, both proxied
 * to the backend (`control.orboto.io`) via nginx:
 *   - getPublicClient: unauth client_name lookup.
 *   - postDecision: operator-authenticated allow/deny → redirect URL.
 */
import { api } from './api';

export interface PublicOAuthClient {
  client_id: string;
  client_name: string;
  redirect_uris: string[];
}

export interface OAuthDecisionInput {
  client_id: string;
  redirect_uri: string;
  scope: string;
  state: string;
  code_challenge: string;
  code_challenge_method: 'S256';
  allow: boolean;
  /** OCP-783 - read-only (default) or the role's writes. */
  access: 'read' | 'write';
}

/** OCP-783 - what the grant carries for the signed-in operator (GET /oauth/consent-info). */
export interface OAuthConsentInfo {
  role: string;
  read_only_requested: boolean;
  access_token_ttl_minutes: number;
  refresh_token_idle_days: number;
  read: { tool_count: number };
  write: {
    tool_count: number;
    destructive: Array<{ name: string; title: string }>;
    account_administration: boolean;
    not_included: string[];
  };
}

export function getOAuthConsentInfo(scope: string): Promise<OAuthConsentInfo> {
  return api<OAuthConsentInfo>(`/oauth/consent-info?scope=${encodeURIComponent(scope)}`);
}

export function getPublicOAuthClient(clientId: string): Promise<PublicOAuthClient> {
  return api<PublicOAuthClient>(`/oauth/clients/${encodeURIComponent(clientId)}/public`);
}

export function postOAuthDecision(input: OAuthDecisionInput): Promise<{ redirect_to: string }> {
  return api<{ redirect_to: string }>('/oauth/authorize/decision', {
    method: 'POST',
    body: input,
  });
}
